import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listClientOrganizations } from "../../../api/adminClientManagementApi";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import AdminTableContainer from "../../../components/midpoint/admin/layout/AdminTableContainer";
import RequirePermission from "../../../auth/RequirePermission";
import StatusBadge from "../../../components/midpoint/intelligence/StatusBadge";
import { formatDateTime } from "../../../components/midpoint/admin/users/adminUserFormatters";

const STATUS_OPTIONS = ["", "ACTIVE", "INACTIVE", "SUSPENDED"];

function AdminClientOrganizationsPageContent() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listClientOrganizations({
        page,
        pageSize: 25,
        search: search || undefined,
        status: status || undefined,
      });
      setData(body);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load client organizations.");
    } finally {
      setLoading(false);
    }
  }, [page, search, status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title="Client Organizations"
        description="Manage client portal organizations and access"
        actions={
          <RequirePermission permission="client_admin.manage_organizations">
            <Link to="/admin/client-organizations/new" className="midpoint-share-button">
              New Organization
            </Link>
          </RequirePermission>
        }
      />

      <div className="admin-toolbar panel-card">
        <label className="admin-users-field">
          <span>Search</span>
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            placeholder="Name, slug, contact…"
          />
        </label>
        <label className="admin-users-field">
          <span>Status</span>
          <select
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s || "all"} value={s}>
                {s || "All"}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? <AdminStatePanel message="Loading organizations…" /> : null}
      {errorMessage ? <AdminStatePanel tone="error" message={errorMessage} /> : null}

      {!loading && data?.organizations?.length === 0 ? (
        <AdminStatePanel message="No client organizations found." />
      ) : null}

      {data?.organizations?.length ? (
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Status</th>
                <th>Contact</th>
                <th>Views</th>
                <th>Users</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.organizations.map((org) => (
                <tr key={org.clientOrgId}>
                  <td>
                    <strong>{org.clientName}</strong>
                    <div className="muted">{org.slug}</div>
                  </td>
                  <td>
                    <StatusBadge label={org.status} tone="neutral" />
                  </td>
                  <td>
                    {org.primaryContactName || "—"}
                    <div className="muted">{org.primaryContactEmail || ""}</div>
                  </td>
                  <td>{org.assignedViewCount}</td>
                  <td>
                    {org.activeUserCount} active
                    {org.invitedUserCount ? ` · ${org.invitedUserCount} invited` : ""}
                  </td>
                  <td>{formatDateTime(org.updatedAt)}</td>
                  <td>
                    <Link to={`/admin/client-organizations/${org.clientOrgId}`}>View</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}

      {data?.pagination?.totalPages > 1 ? (
        <div className="admin-table-footer panel-card">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span className="muted">
            Page {data.pagination.page} of {data.pagination.totalPages}
          </span>
          <button
            type="button"
            disabled={page >= data.pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      ) : null}
    </AdminPageLayout>
  );
}

const AdminClientOrganizationsPage = () => (
  <RequirePermission permission="client_admin.view">
    <AdminClientOrganizationsPageContent />
  </RequirePermission>
);

export default AdminClientOrganizationsPage;
